import React, { useState, useEffect, useCallback } from "react";
import { Timestamp } from "../../../firebase";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { validation } from "../../../constants";
import { ApplicantDetails } from "../../templates/ApplicantDetails";
import { message } from "antd";
import moment from "moment";
import { ApplicantDocument } from "../../../core/entities/applicant";

const ApplicantDetailsPage = ({
  isLoading,
  isOverlap,
  isChecking,
  resetIsOverlap,
  checkSchedule,
  isLoadingFile,
  applicant,
  fetchApplicant,
  updateApplicant,
  isUpdating,
  error,
  isUpdateSuccessful,
  resetIsUpdateSuccessful,
  user,
  updateLogs,
  fetchComments,
  comments,
  isCommentsLoading,
  updateComments,
  getLogs,
  logs,
  updateLogsRequest,
  updateApplicantRequest,
  fetchUsers,
  updateTotalNewApplicants,
  addNotification,
  interviewers,
  clearApplicantRequest,
  isSearchUsersLoading,
  downloadDocument,
  uploadApplicantDocument,
  isUploadApplicantDocumentsLoading,
  isUploadApplicantDocumentsSuccessful,
  deleteApplicantDocuments,
  isDeleteApplicantDocumentsLoading,
  isDeleteApplicantDocumentsSuccessful,
}: any) => {
  const [comment, setComment] = useState("");
  const history = useHistory();
  const location = useLocation();
  const navigationParams: {
    id: string;
    fromSearch?: string | undefined;
  } = useParams();
  const { id, fromSearch } = navigationParams;
  const userAccessType = user && user.accessType;

  useEffect(() => {
    if (
      (updateLogsRequest?.didSucceed && !updateLogsRequest?.isLoading) ||
      (updateApplicantRequest?.didSucceed && !updateApplicantRequest?.isLoading)
    ) {
      fetchApplicant(id);
    }
    if (
      updateApplicantRequest?.didSucceed &&
      updateApplicantRequest?.response?.interviewers &&
      Object.getOwnPropertyNames(updateApplicantRequest?.response).length > 1
    ) {
      clearApplicantRequest();
      message.config({ duration: 7, maxCount: 1 });
      message.success("Successfully sent an invite/event.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateLogsRequest?.didSucceed, updateApplicantRequest?.didSucceed]);

  useEffect(() => {
    if (
      !updateApplicantRequest?.didSucceed &&
      !updateApplicantRequest?.isLoading &&
      error
    ) {
      fetchApplicant(id);
      message.config({ duration: 7, maxCount: 1 });
      message.error(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateApplicantRequest?.isLoading]);

  useEffect(() => {
    if (
      isUpdateSuccessful === null &&
      applicant &&
      applicant.applicationStatus &&
      applicant.applicationStatus.subStatus === "Unread"
    ) {
      updateTotalNewApplicants("New Applicant", "New Applicant", "Read");

      updateApplicant(id, {
        applicationStatus: {
          ...applicant.applicationStatus,
          subStatus: "Read",
          dateMoved: Timestamp.now(),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicant]);

  useEffect(() => {
    resetIsUpdateSuccessful();
    fetchApplicant(id);
    fetchComments(id);

    return () => {
      resetIsUpdateSuccessful();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCommentChange = (e: any, newValue: any, newPlainTextValue: any, mentions: any) => {
    setComment(e.target.value);
  };

  const onComment = () => {
    if (!validation.isEmptyOrSpaces(comment)) {
      setComment("");

      const currentComment = [
        {
          commentor: user.name,
          comment: comment,
          dateTime: Timestamp.now(),
        },
      ];

      if (comments.comment && comments.comment.length) {
        const pastComments = comments?.comment.map((comment: any) => {
          const { dateTime, ...otherData } = comment;
          return {
            dateTime: Timestamp.fromDate(dateTime),
            ...otherData,
          };
        });

        currentComment.push(...pastComments);
      }

      const { firstName, lastName, middleName } = applicant.applicantName;
      const applicantName = `${firstName} ${middleName} ${lastName}`;
      let description = `${user.name} commented on ${applicantName}`;

      const notificationData = {
        type: "commented",
        title: description,
        description: comment,
        applicantName: `${applicantName}`,
        user: { id: user.id, name: user.name },
        notificationLink: location.pathname,
      };

      updateComments(id, comments.id, currentComment);
      addNotification(notificationData);
    }
  };

  const onBack = () => {
    return history.goBack();
  };

  const openResume = () => {
    // code for opening resume
    alert("resume open");
  };

  const onModalConfirm = async (applicationStatus: {
    currentStatus: string;
    finalInterview: boolean;
    initialInterview: boolean;
    jobOffer: boolean;
    newApplicant: boolean;
    psychological: boolean;
    shortlisted: boolean;
    subStatus: string;
  }) => {
    if (
      applicationStatus.currentStatus !==
      applicant.applicationStatus.currentStatus
    ) {
      const newLog = {
        dateTime: Timestamp.now(),
        status: `Moved to ${applicationStatus.currentStatus}`,
        editor: user.name,
      };

      await updateLogs(id, newLog);
      await updateTotalNewApplicants(
        applicationStatus.currentStatus,
        applicant.applicationStatus.currentStatus,
        ""
      );
    }

    const newApplicationStatus = {
      ...applicationStatus,
      dateMoved: Timestamp.now(),
    };

    if (applicationStatus.subStatus === "Unread") {
      await updateTotalNewApplicants(
        "New Applicant",
        "New Applicant",
        "Unread"
      );
    }

    if (applicationStatus.currentStatus === "Job Offer") {
      const { firstName, lastName, middleName } = applicant.applicantName;
      const applicantName = `${firstName} ${middleName} ${lastName}`;

      const notificationData = {
        type: "move",
        title: `${applicantName} is moved to Job Offer`,
        description: `${applicantName} is moved to Job Offer`,
        user: { id: user.id, name: user.name },
        notificationLink: `/recruit/job-offer/${id}`,
      };

      await addNotification(notificationData);
    }

    await updateApplicant(id, { applicationStatus: newApplicationStatus });
  };

  const handleOnUpdateApplicantStatus = async (params: any) => {
    const { firstName, lastName, middleName } = applicant.applicantName;
    const applicantName = `${firstName} ${middleName} ${lastName}`;

    const currentStatus = applicant?.applicationStatus?.currentStatus;
    const phase =
      currentStatus === "Initial Interview"
        ? "initialInterview"
        : currentStatus === "Final Interview"
        ? "finalInterview"
        : "jobOffer";

    if (
      params.hasOwnProperty("scheduleDate.initialInterview") ||
      params.hasOwnProperty("scheduleDate.finalInterview") ||
      params.hasOwnProperty("scheduleDate.jobOffer")
    ) {
      if (applicant?.scheduleDate && applicant?.scheduleDate[phase]) {
        const newLog = {
          dateTime: Timestamp.now(),
          status: "Rescheduled",
          editor: user.name,
        };
        await updateLogs(id, newLog);
      } else {
        const dateTime = moment(params[`scheduleDate.${phase}`]).format(
          "DD/MM/YY | h:mm A"
        );
        const notificationData = {
          type: "schedule",
          title: `${
            user.name
          } scheduled ${applicant?.applicationStatus?.currentStatus?.toLowerCase()} for ${applicantName} on ${dateTime}`,
          description: `${
            user.name
          } scheduled ${applicant?.applicationStatus?.currentStatus?.toLowerCase()} for ${applicantName} on ${dateTime}`,
          user: { id: user.id, name: user.name },
          notificationLink: location.pathname,
          interviewers: params[`interviewers`]
            ? params[`interviewers`][phase]["interviewers"]
            : [],
          interviewerDescription: params[`interviewers`]
            ? `You are invited as an interviewer for ${applicantName}'s ${applicant?.applicationStatus?.currentStatus} on ${dateTime}`
            : "",
        };

        await addNotification(notificationData);
      }
      if (
        !applicant?.interviewers ||
        applicant?.interviewers[phase] === undefined ||
        !applicant?.scheduleDate ||
        applicant?.scheduleDate[phase] === undefined
      ) {
        await updateLogs(id, {
          dateTime: Timestamp.now(),
          status: `Invited for ${currentStatus}`,
          editor: `${user.name} (${moment(
            params[`scheduleDate.${phase}`]
          ).format("MM/DD/YYYY | h:mma")})`,
        });
      }
    }
    if (params["applicationStatus.currentStatus"] === "Failed") {
      await updateTotalNewApplicants(
        "Failed",
        params["applicationStatus.failedPhase"],
        ""
      );
    }

    if (params["applicationStatus.currentStatus"] === "Job Offer") {
      const notificationData = {
        type: "move",
        title: `${applicantName} is moved to Job Offer`,
        description: `${applicantName} is moved to Job Offer`,
        user: { id: user.id, name: user.name },
        notificationLink: `/recruit/job-offer/${id}`,
      };

      await addNotification(notificationData);
    }

    if (params["applicationStatus.subStatus"]) {
      const notificationData = {
        type: "response",
        read: false,
        title: `${user.name} updated ${applicantName} application status to ${params["applicationStatus.subStatus"]} - ${applicant.applicationStatus.currentStatus}`,
        description: `${user?.name} updated ${applicantName} application status to ${params["applicationStatus.subStatus"]} - ${applicant.applicationStatus.currentStatus}`,
        notificationLink: `/recruit/${applicant.applicationStatus.currentStatus
          .replace(" ", "-")
          .toLowerCase()}/${id}`,
        user: { id: user.id, name: user.name },
      };

      await addNotification(notificationData);
    }

    await updateApplicant(id, params);
  };

  const handleUploadApplicantDocuments = useCallback((file: Blob, fileName: string) => 
    uploadApplicantDocument(id, file, fileName)
  , [id, uploadApplicantDocument]);

  const handleDeleteApplicantDocuments = useCallback((document: ApplicantDocument) => 
    deleteApplicantDocuments(id, document)
  , [id, deleteApplicantDocuments]);

  return (
    <ApplicantDetails
      onBack={onBack}
      applicantDetails={applicant}
      openResume={openResume}
      downloadDocument={downloadDocument}
      onComment={onComment}
      comments={comments?.comment}
      comment={comment}
      onCommentChange={onCommentChange}
      onModalConfirm={onModalConfirm}
      isLoadingFile={isLoadingFile}
      isLoading={isLoading}
      isOverlap={isOverlap}
      isChecking={isChecking}
      resetIsOverlap={resetIsOverlap}
      checkSchedule={checkSchedule}
      isUpdating={isUpdating}
      error={error}
      isUpdateSuccessful={isUpdateSuccessful}
      onUpdateApplicantStatus={handleOnUpdateApplicantStatus}
      resetIsUpdateSuccessful={resetIsUpdateSuccessful}
      isCommentsLoading={isCommentsLoading}
      accessType={userAccessType}
      fromSearch={fromSearch}
      getLogs={getLogs}
      logs={logs}
      interviewers={interviewers}
      updateApplicantRequest={updateApplicantRequest}
      searchUser={fetchUsers}
      user={user}
      isSearchUsersLoading={isSearchUsersLoading}
      onUploadApplicantDocuments={handleUploadApplicantDocuments}
      isUploadApplicantDocumentsLoading={isUploadApplicantDocumentsLoading}
      isUploadApplicantDocumentsSuccessful={isUploadApplicantDocumentsSuccessful}
      deleteApplicantDocuments={handleDeleteApplicantDocuments}
      isDeleteApplicantDocumentsLoading={isDeleteApplicantDocumentsLoading}
      isDeleteApplicantDocumentsSuccessful={isDeleteApplicantDocumentsSuccessful}
    />
  );
};

export default ApplicantDetailsPage;
